import React,{useState} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import SafeIcon from '../SafeIcon';
import {FiX,FiType,FiDroplet,FiCheck,FiAlertCircle} from 'react-icons/fi';

const StyleMenuPopup=({onClose,onUpdate,currentStyles={}})=> {
  const [styles,setStyles]=useState({
    backgroundColor: currentStyles.backgroundColor || '#ffffff',
    textColor: currentStyles.textColor || '#4b5563',
    hoverColor: currentStyles.hoverColor || '#2dd4bf',
    buttonBgColor: currentStyles.buttonBgColor || '#111827',
    buttonTextColor: currentStyles.buttonTextColor || '#ffffff',
    ...currentStyles
  });

  const presetBackgrounds=[
    {name:'White',color:'#ffffff'},
    {name:'Light Gray',color:'#f9fafb'},
    {name:'Gray',color:'#f3f4f6'},
    {name:'Dark',color:'#1f2937'},
    {name:'Teal',color:'#14b8a6'},
    {name:'Blue',color:'#3b82f6'},
  ];

  const presetTextColors=[
    {name:'Gray',color:'#4b5563'},
    {name:'Dark',color:'#111827'},
    {name:'White',color:'#ffffff'},
    {name:'Teal',color:'#2dd4bf'},
    {name:'Blue',color:'#3b82f6'},
  ];

  const presetHoverColors=[
    {name:'Teal',color:'#2dd4bf'},
    {name:'Blue',color:'#3b82f6'},
    {name:'Pink',color:'#ec4899'},
    {name:'Purple',color:'#8b5cf6'},
    {name:'Orange',color:'#f97316'},
  ];

  const handleSave=()=> {
    onUpdate(styles);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <motion.div 
        initial={{opacity: 0,scale: 0.95,y: 20}} 
        animate={{opacity: 1,scale: 1,y: 0}} 
        className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/80">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-br from-teal-400 to-blue-500 rounded-2xl">
              <SafeIcon icon={FiType} className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-black text-gray-900 text-xl uppercase tracking-tight">Style Menu</h3>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Customize navigation appearance</p>
            </div>
          </div>
          <button onClick={onClose} className="bg-white p-2.5 rounded-xl shadow-sm hover:shadow-md transition-all text-gray-400 hover:text-gray-600 border border-gray-100">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
          
          {/* Menu Background */}
          <div className="space-y-3">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center">
              <SafeIcon icon={FiDroplet} className="mr-2" />
              Menu Background
            </label>
            <div className="grid grid-cols-6 gap-2">
              {presetBackgrounds.map((preset)=> (
                <button
                  key={preset.name}
                  onClick={()=> setStyles({...styles,backgroundColor: preset.color})}
                  className={`aspect-square rounded-xl border-3 transition-all hover:scale-110 ${
                    styles.backgroundColor === preset.color 
                      ? 'border-teal-500 ring-2 ring-teal-200 shadow-lg' 
                      : 'border-gray-100 shadow-sm'
                  }`}
                  style={{backgroundColor: preset.color}}
                  title={preset.name}
                />
              ))}
              <div className="relative aspect-square rounded-xl border-3 border-gray-100 shadow-sm overflow-hidden group">
                <input 
                  type="color" 
                  value={styles.backgroundColor}
                  onChange={(e)=> setStyles({...styles,backgroundColor: e.target.value})}
                  className="absolute inset-0 w-[200%] h-[200%] -top-1/2 -left-1/2 cursor-pointer opacity-0"
                />
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-400 to-gray-600">
                  <SafeIcon icon={FiDroplet} className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Menu Text Color */}
          <div className="space-y-3">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center">
              <SafeIcon icon={FiType} className="mr-2" />
              Menu Text Color
            </label>
            <div className="grid grid-cols-6 gap-2">
              {presetTextColors.map((preset)=> (
                <button
                  key={preset.name}
                  onClick={()=> setStyles({...styles,textColor: preset.color})}
                  className={`aspect-square rounded-xl border-3 transition-all hover:scale-110 ${
                    styles.textColor === preset.color 
                      ? 'border-teal-500 ring-2 ring-teal-200 shadow-lg' 
                      : 'border-gray-100 shadow-sm'
                  }`}
                  style={{backgroundColor: preset.color}}
                  title={preset.name}
                />
              ))}
              <div className="relative aspect-square rounded-xl border-3 border-gray-100 shadow-sm overflow-hidden group">
                <input 
                  type="color" 
                  value={styles.textColor}
                  onChange={(e)=> setStyles({...styles,textColor: e.target.value})}
                  className="absolute inset-0 w-[200%] h-[200%] -top-1/2 -left-1/2 cursor-pointer opacity-0"
                />
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-400 to-gray-600">
                  <SafeIcon icon={FiDroplet} className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Hover Color */}
          <div className="space-y-3">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center">
              Hover Color
            </label>
            <div className="grid grid-cols-6 gap-2">
              {presetHoverColors.map((preset)=> (
                <button
                  key={preset.name}
                  onClick={()=> setStyles({...styles,hoverColor: preset.color})}
                  className={`aspect-square rounded-xl border-3 transition-all hover:scale-110 ${
                    styles.hoverColor === preset.color 
                      ? 'border-teal-500 ring-2 ring-teal-200 shadow-lg' 
                      : 'border-gray-100 shadow-sm'
                  }`}
                  style={{backgroundColor: preset.color}}
                  title={preset.name}
                />
              ))}
              <div className="relative aspect-square rounded-xl border-3 border-gray-100 shadow-sm overflow-hidden group">
                <input 
                  type="color" 
                  value={styles.hoverColor}
                  onChange={(e)=> setStyles({...styles,hoverColor: e.target.value})}
                  className="absolute inset-0 w-[200%] h-[200%] -top-1/2 -left-1/2 cursor-pointer opacity-0"
                />
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-400 to-gray-600">
                  <SafeIcon icon={FiDroplet} className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Button Background */}
          <div className="space-y-3">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center">
              Button Background
            </label>
            <div className="grid grid-cols-6 gap-2">
              {presetBackgrounds.slice(0,5).map((preset)=> (
                <button
                  key={preset.name}
                  onClick={()=> setStyles({...styles,buttonBgColor: preset.color})}
                  className={`aspect-square rounded-xl border-3 transition-all hover:scale-110 ${
                    styles.buttonBgColor === preset.color 
                      ? 'border-teal-500 ring-2 ring-teal-200 shadow-lg' 
                      : 'border-gray-100 shadow-sm'
                  }`}
                  style={{backgroundColor: preset.color}}
                  title={preset.name}
                />
              ))}
              <div className="relative aspect-square rounded-xl border-3 border-gray-100 shadow-sm overflow-hidden group">
                <input 
                  type="color" 
                  value={styles.buttonBgColor}
                  onChange={(e)=> setStyles({...styles,buttonBgColor: e.target.value})}
                  className="absolute inset-0 w-[200%] h-[200%] -top-1/2 -left-1/2 cursor-pointer opacity-0"
                />
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-400 to-gray-600">
                  <SafeIcon icon={FiDroplet} className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Button Text Color */}
          <div className="space-y-3">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center">
              Button Text Color
            </label>
            <div className="grid grid-cols-6 gap-2">
              {presetTextColors.slice(0,5).map((preset)=> (
                <button
                  key={preset.name}
                  onClick={()=> setStyles({...styles,buttonTextColor: preset.color})}
                  className={`aspect-square rounded-xl border-3 transition-all hover:scale-110 ${
                    styles.buttonTextColor === preset.color 
                      ? 'border-teal-500 ring-2 ring-teal-200 shadow-lg' 
                      : 'border-gray-100 shadow-sm'
                  }`}
                  style={{backgroundColor: preset.color}}
                  title={preset.name}
                />
              ))}
              <div className="relative aspect-square rounded-xl border-3 border-gray-100 shadow-sm overflow-hidden group">
                <input 
                  type="color" 
                  value={styles.buttonTextColor}
                  onChange={(e)=> setStyles({...styles,buttonTextColor: e.target.value})}
                  className="absolute inset-0 w-[200%] h-[200%] -top-1/2 -left-1/2 cursor-pointer opacity-0"
                />
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-400 to-gray-600">
                  <SafeIcon icon={FiDroplet} className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="p-4 rounded-2xl border border-gray-100" style={{backgroundColor: styles.backgroundColor}}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <span style={{color: styles.textColor}} className="text-sm font-bold">Home</span>
                <span style={{color: styles.textColor}} className="text-sm font-bold">Courses</span>
                <span style={{color: styles.textColor}} className="text-sm font-bold">Community</span>
              </div>
              <button 
                className="px-6 py-2 font-bold text-sm rounded-xl"
                style={{backgroundColor: styles.buttonBgColor, color: styles.buttonTextColor}}
              >
                Enroll Now
              </button>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="p-6 bg-gray-50 border-t border-gray-100 flex space-x-4">
          <button onClick={onClose} className="flex-1 py-4 font-black text-gray-400 hover:text-gray-600 uppercase tracking-widest text-xs transition-colors">
            Discard
          </button>
          <button onClick={handleSave} className="flex-[2] py-4 bg-gray-900 text-white font-black uppercase tracking-widest text-xs rounded-2xl shadow-xl shadow-gray-200 hover:bg-teal-600 hover:shadow-teal-100 transition-all">
            Apply Styles
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default StyleMenuPopup;